import React, { useEffect, useMemo, useState } from 'react';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useMutation } from '@apollo/client';
import { LoadingSpinner } from '@cvent/carina/components/LoadingSpinner';
import { TransferWithIdDocument } from '@cvent/universal-login-web-graphql/operations';
import { Logger } from '@cvent/nucleus-logging';

import { SessionManager } from '@api/okta';
import { HardError } from '@components/errors/HardError';

const LOG = new Logger('SignOnPage');

export const getServerSideProps: GetServerSideProps = async ctx => {
  const query = ctx.query;

  if (!('transferId' in query)) {
    LOG.error('Missing query param transferId');

    return {
      notFound: true
    };
  }

  return {
    props: {
      transferId: query.transferId
    }
  };
};

const TEST_ID = 'signOn';
export const TransferPage = ({ transferId }: InferGetServerSidePropsType<typeof getServerSideProps>): JSX.Element => {
  const [error, setError] = useState(undefined);
  const sessionManager = useMemo(() => {
    return new SessionManager();
  }, []);

  const [createTransferMagicLink, { called }] = useMutation(TransferWithIdDocument, {
    variables: {
      transferId
    }
  });

  useEffect(() => {
    /**
     * We must ensure we close sessions for managed users as we will be re-initiating
     * their Okta session when redirecting through the magic link contained within our transfer state.
     */
    const handleTransferMagicLink = async (): Promise<void> => {
      const {
        data: { transferMagicLink }
      } = await createTransferMagicLink();

      if (transferMagicLink.statusCode === 200) {
        const { userType, href } = transferMagicLink.detail;

        await sessionManager.closeSession(userType);

        location.assign(href);
      } else {
        setError(new HardError({ ...transferMagicLink }));
      }
    };

    if (!called) handleTransferMagicLink();
  }, [called, createTransferMagicLink]);

  if (error) throw error;

  return <LoadingSpinner testID={`${TEST_ID}_loading`} />;
};
export default TransferPage;
